import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import {
    AreaChart,
    Area,
    ResponsiveContainer,
} from 'recharts';

interface AnimatedKPICardProps {
    title: string;
    value: number;
    previousValue?: number;
    format?: 'currency' | 'number' | 'percentage';
    icon: React.ReactNode;
    trendData?: number[];
    colorScheme?: 'primary' | 'success' | 'warning' | 'danger' | 'blue';
    subtitle?: string;
    onClick?: () => void;
}

const formatValue = (value: number, format: 'currency' | 'number' | 'percentage'): string => {
    switch (format) {
        case 'currency':
            if (value >= 10000000) {
                return `₹${(value / 10000000).toFixed(2)}Cr`;
            } else if (value >= 100000) {
                return `₹${(value / 100000).toFixed(2)}L`;
            } else if (value >= 1000) {
                return `₹${(value / 1000).toFixed(1)}K`;
            }
            return `₹${value.toLocaleString('en-IN')}`;
        case 'percentage':
            return `${value.toFixed(1)}%`;
        case 'number':
        default:
            if (value >= 1000000) {
                return `${(value / 1000000).toFixed(1)}M`;
            } else if (value >= 1000) {
                return `${(value / 1000).toFixed(1)}K`;
            }
            return value.toLocaleString('en-IN');
    }
};

const colorSchemes = {
    primary: {
        bg: 'from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-800/10',
        border: 'border-orange-200 dark:border-orange-800',
        iconBg: 'bg-orange-100 dark:bg-orange-900/50',
        iconColor: 'text-orange-600 dark:text-orange-400',
        valueColor: 'text-orange-600 dark:text-orange-400',
        sparklineColor: '#f97316',
    },
    success: {
        bg: 'from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10',
        border: 'border-emerald-200 dark:border-emerald-800',
        iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
        valueColor: 'text-emerald-600 dark:text-emerald-400',
        sparklineColor: '#10b981',
    },
    warning: {
        bg: 'from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10',
        border: 'border-amber-200 dark:border-amber-800',
        iconBg: 'bg-amber-100 dark:bg-amber-900/50',
        iconColor: 'text-amber-600 dark:text-amber-400',
        valueColor: 'text-amber-600 dark:text-amber-400',
        sparklineColor: '#f59e0b',
    },
    danger: {
        bg: 'from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10',
        border: 'border-red-200 dark:border-red-800',
        iconBg: 'bg-red-100 dark:bg-red-900/50',
        iconColor: 'text-red-600 dark:text-red-400',
        valueColor: 'text-red-600 dark:text-red-400',
        sparklineColor: '#ef4444',
    },
    blue: {
        bg: 'from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10',
        border: 'border-blue-200 dark:border-blue-800',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50',
        iconColor: 'text-blue-600 dark:text-blue-400',
        valueColor: 'text-blue-600 dark:text-blue-400',
        sparklineColor: '#3b82f6',
    },
};

// Hook for count-up animation
function useCountUp(end: number, duration: number = 1500): number {
    const [count, setCount] = useState(0);
    const startTime = useRef<number | null>(null);
    const prevEnd = useRef(end);

    useEffect(() => {
        // Reset if end value changed significantly
        if (Math.abs(prevEnd.current - end) > end * 0.1) {
            setCount(0);
            startTime.current = null;
        }
        prevEnd.current = end;

        const animate = (timestamp: number) => {
            if (startTime.current === null) {
                startTime.current = timestamp;
            }

            const progress = Math.min((timestamp - startTime.current) / duration, 1);
            // Easing function for smooth deceleration
            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            setCount(Math.floor(end * easeOutQuart));

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        const animationId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationId);
    }, [end, duration]);

    return count;
}

// Mini sparkline component
function MiniSparkline({ data, color }: { data: number[]; color: string }) {
    const chartData = data.map((value, index) => ({ index, value }));

    return (
        <ResponsiveContainer width="100%" height={40}>
            <AreaChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <defs>
                    <linearGradient id={`sparkline-gradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <Area
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={1.5}
                    fill={`url(#sparkline-gradient-${color.replace('#', '')})`}
                    dot={false}
                    isAnimationActive={true}
                    animationDuration={1000}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
}

// Trend badge component
function TrendBadge({ current, previous }: { current: number; previous: number }) {
    const percentChange = previous > 0 ? ((current - previous) / previous) * 100 : 0;
    const isPositive = percentChange > 0;
    const isNeutral = Math.abs(percentChange) < 0.1;

    if (isNeutral) {
        return (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800">
                <Minus className="h-3 w-3 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">0%</span>
            </div>
        );
    }

    return (
        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${isPositive
                ? 'bg-emerald-100 dark:bg-emerald-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            }`}>
            {isPositive ? (
                <ArrowUpRight className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            ) : (
                <ArrowDownRight className="h-3 w-3 text-red-600 dark:text-red-400" />
            )}
            <span className={`text-xs font-medium ${isPositive
                    ? 'text-emerald-700 dark:text-emerald-400'
                    : 'text-red-700 dark:text-red-400'
                }`}>
                {Math.abs(percentChange).toFixed(1)}%
            </span>
        </div>
    );
}

export function AnimatedKPICard({
    title,
    value,
    previousValue,
    format = 'number',
    icon,
    trendData,
    colorScheme = 'primary',
    subtitle,
    onClick,
}: AnimatedKPICardProps) {
    const animatedValue = useCountUp(value, 1500);
    const colors = colorSchemes[colorScheme];

    return (
        <Card
            className={`
                relative overflow-hidden cursor-pointer
                bg-gradient-to-br ${colors.bg}
                border ${colors.border}
                hover:shadow-lg hover:scale-[1.02] transition-all duration-300
                group
            `}
            onClick={onClick}
        >
            {/* Decorative background element */}
            <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10 group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/5 group-hover:scale-110 transition-transform duration-700" />

            <CardContent className="p-6 relative">
                <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${colors.iconBg} shadow-sm`}>
                        <div className={colors.iconColor}>
                            {icon}
                        </div>
                    </div>
                    {previousValue !== undefined && (
                        <TrendBadge current={value} previous={previousValue} />
                    )}
                </div>

                <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">{title}</p>
                    <p className={`text-3xl font-bold ${colors.valueColor} tracking-tight`}>
                        {formatValue(animatedValue, format)}
                    </p>
                    {subtitle && (
                        <p className="text-xs text-muted-foreground">{subtitle}</p>
                    )}
                </div>

                {trendData && trendData.length > 0 && (
                    <div className="mt-4 -mx-2 -mb-2">
                        <MiniSparkline data={trendData} color={colors.sparklineColor} />
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default AnimatedKPICard;
